import { t } from '@lingui/core/macro';
import { UserGroupDataQualityField, UserGroupDataQualityProblem } from '@navio/nsdk';
import type { UserGroupDataQualityPromptTranslations } from 'noddi-web';

/**
 * Issue copy composed from field names and per-problem sentences rather than a
 * 27-cell map, so a new backend field costs one string here and one catalog
 * entry per locale. The `Record` typing in noddi-web is the completeness guard.
 *
 * Called at render time so Lingui resolves the active locale; the record's
 * `isPersonal` picks the title.
 */
export const getUserGroupDataQualityTranslations = (): UserGroupDataQualityPromptTranslations => ({
  title: (userGroup) => {
    // An organization check mixes the signed-in user's personal details with
    // shared org fields, so neither "yours" nor the org name is accurate.
    if (!userGroup.isPersonal) {
      return t`We suspect some details are incorrect`;
    }
    return t`We suspect some of your details are incorrect`;
  },
  description: t`This may prevent you from making bookings. Please review and update your details.`,
  membersTitle: t`Members with incomplete details`,
  fieldLabels: {
    [UserGroupDataQualityField.EMAIL]: t`Email`,
    [UserGroupDataQualityField.INVOICE_ADDRESS]: t`Invoice address`,
    [UserGroupDataQualityField.MOBILE_PHONE_NUMBER]: t`Mobile phone number`,
    [UserGroupDataQualityField.NAME]: t`Customer name`,
    [UserGroupDataQualityField.ORGANIZATION_NUMBER]: t`Organization number`,
    [UserGroupDataQualityField.PRIMARY_CONTACT]: t`Primary contact`,
    [UserGroupDataQualityField.LEASE_CONTACT_NAME]: t`Lease contact name`,
    [UserGroupDataQualityField.LEASE_CONTACT_MOBILE_PHONE_NUMBER]: t`Lease contact mobile phone number`,
    [UserGroupDataQualityField.LEASE_CONTACT_EMAIL]: t`Lease contact email`
  },
  problemTemplates: {
    [UserGroupDataQualityProblem.MISSING]: (field) => t`${field} is missing`,
    [UserGroupDataQualityProblem.INVALID]: (field) => t`${field} is invalid`,
    [UserGroupDataQualityProblem.DUPLICATE]: (field) => t`${field} is also used by another account`
  },
  issueOverrides: {
    [UserGroupDataQualityField.INVOICE_ADDRESS]: {
      [UserGroupDataQualityProblem.INVALID]: t`Invoice address is incomplete`,
      [UserGroupDataQualityProblem.DUPLICATE]: t`Invoice address is shared with another account`
    },
    [UserGroupDataQualityField.MOBILE_PHONE_NUMBER]: {
      [UserGroupDataQualityProblem.INVALID]: t`Phone number must be a valid mobile number`
    },
    [UserGroupDataQualityField.LEASE_CONTACT_MOBILE_PHONE_NUMBER]: {
      [UserGroupDataQualityProblem.INVALID]: t`Lease contact phone number must be a valid mobile number`
    }
  },
  errorTitle: t`Could not check booking details`,
  errorMessage: t`Try refreshing the page.`
});
